<script setup lang="ts">
import ChartPieIcon from '@/assets/img/svg/icons/chart-pie-outline.svg'
import ChartBarIcon from '@/assets/img/svg/icons/chart-bar-outline.svg'
import type { PeriodRange } from '@/types/Common'
import type { ChartInterval } from 'shared'
import { type ChartProps } from '@/types/Charts'
import { onMounted, onBeforeUnmount, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface IntervalOption {
  label: string
  value: ChartInterval
}
const { t } = useI18n({ useScope: 'global' })

const props = defineProps<{
  title?: string
  chartProps: ChartProps
  intervalOptions: IntervalOption[]
  showChartToggle?: boolean
  showChartIntervalDropdown?: boolean
  chartView?: 'bar' | 'pie'
  setChartView?: (view: 'bar' | 'pie') => void
}>()

const open = defineModel<boolean>('open', { default: false })
const chartInterval = defineModel<ChartInterval>('chartInterval', { default: 'week' })
const chartPeriod = defineModel<PeriodRange>('chartPeriod', {
  default: () => ({
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
  }),
})

// Logic to prevent chart blinking on load and height changes
const chartWrapperRef = ref<HTMLElement | null>(null)
const lastHeight = ref<number | null>(null)
let resizeObserver: ResizeObserver | null = null

const wrapperStyle = computed(() => {
  return lastHeight.value ? { height: `${String(lastHeight.value)}px` } : {}
})

onMounted(() => {
  if (!chartWrapperRef.value) return
  resizeObserver = new ResizeObserver((entries) => {
    const entry = entries[0]
    if (!entry.contentRect.height) return
    lastHeight.value = entry.contentRect.height
  })
  resizeObserver.observe(chartWrapperRef.value)
})

onBeforeUnmount(() => {
  if (resizeObserver && chartWrapperRef.value) {
    resizeObserver.unobserve(chartWrapperRef.value)
  }
  resizeObserver = null
})
</script>

<template>
  <OModal
    v-model:open="open"
    closable
    size="large"
    :title="props.title ? t(props.title) : undefined"
    :display-footer="false"
    content-class="min-h-fit p-6 sm:p-6 min-w-[968px]"
    scrollable-content-class="px-0 !mt-6"
  >
    <template #content>
      <div class="border border-(--base-blue-50) rounded-lg px-4 py-6">
        <div class="flex justify-start gap-3 mb-4">
          <!-- v-if="props.showChartIntervalDropdown ?? props.chartProps.chartType === 'bar'" -->
          <ODropdown
            v-if="props.showChartIntervalDropdown"
            class="!w-fit"
            v-model="chartInterval"
            :options="props.intervalOptions"
            medium
            filter
          />
          <OCalendar v-model="chartPeriod" size="L" simple-range teleport />
          <div v-if="props.showChartToggle" class="ml-auto flex items-center gap-3">
            <OButton
              :class="['size-5 chart-btn', props.chartView === 'pie' && 'chart-btn--selected']"
              size="S"
              variant="tertiary"
              @click="props.setChartView?.('pie')"
              :hoverable="false"
              :outline="false"
            >
              <template #icon><ChartPieIcon class="size-5" /></template>
            </OButton>

            <OButton
              :class="['size-5 chart-btn', props.chartView === 'bar' && 'chart-btn--selected']"
              size="S"
              variant="tertiary"
              @click="props.setChartView?.('bar')"
              :hoverable="false"
              :outline="false"
            >
              <template #icon><ChartBarIcon class="size-5" /></template>
            </OButton>
          </div>
        </div>
        <div
          data-testid="chart-wrapper"
          class="mt-4 relative h-[420px]"
          ref="chartWrapperRef"
          :style="wrapperStyle"
        >
          <OChart
            wrapper-class="shadow-none !p-0 h-full"
            :chartType="props.chartProps.chartType"
            :period="chartInterval"
            :series="props.chartProps.series"
            :categories="props.chartProps.categories"
            :rawCategories="props.chartProps.rawCategories"
            :colors="props.chartProps.colors"
            :loading="props.chartProps.loading"
            :display-legend="props.chartView === 'pie'"
            :tooltipFormat="props.chartProps.tooltipFormat"
            :highlight-bar="props.chartProps.highlightBar"
            :highlight-bar-index="props.chartProps.highlightBarIndex"
            :chart-min-height="300"
            :legend-collapsed-lines="2"
          />
          <div v-if="props.chartProps.loading" class="chart-overlay">
            <OSpinner size="L" />
          </div>
        </div>
      </div>
    </template>
  </OModal>
</template>

<style scoped>
.chart-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.64);
  backdrop-filter: blur(2px);
  transition: opacity 200ms ease;
  pointer-events: none;
}

.chart-btn {
  color: var(--text-neutral-3);
  position: relative;
}

.chart-btn::after {
  content: '';
  position: absolute;
  left: 2px;
  right: 2px;
  bottom: 0;
  height: 1.5px;
  background: var(--text-primary-interactive);
  transform: translateY(4px);
  opacity: 0;
  pointer-events: none;
  transition:
    transform 300ms ease,
    opacity 300ms ease;
}

.chart-btn--selected {
  color: var(--text-primary-interactive);
}

.chart-btn--selected::after {
  transform: translateY(2px);
  opacity: 1;
}
</style>
